/**
 * Service to manage translations for size charts
 */

// Default translations for common size chart elements
export const defaultTranslations = {
  // Language options
  languages: [
    { value: 'en', label: 'English' },
    { value: 'de', label: 'German (Deutsch)' },
    { value: 'fr', label: 'French (Français)' },
    { value: 'es', label: 'Spanish (Español)' },
    { value: 'it', label: 'Italian (Italiano)' },
    { value: 'nl', label: 'Dutch (Nederlands)' },
    { value: 'pt', label: 'Portuguese (Português)' },
    { value: 'ja', label: 'Japanese (日本語)' },
    { value: 'zh', label: 'Chinese (中文)' },
    { value: 'ru', label: 'Russian (Русский)' }
  ],
  
  // Common size chart elements translations
  elements: {
    // English (default)
    en: {
      // Section titles
      sizeChart: 'Size Chart',
      howToMeasure: 'How to Measure',
      fitGuide: 'Fit Guide',
      notes: 'Notes',
      
      // Fit types
      slim: 'Slim',
      regular: 'Regular',
      loose: 'Loose',
      oversized: 'Oversized',
      
      // Stretch levels
      none: 'No Stretch',
      slight: 'Slight Stretch',
      moderate: 'Moderate Stretch',
      stretchy: 'Stretchy',
      high: 'High Stretch',
      
      // Common measurement points
      bust: 'Bust',
      chest: 'Chest',
      waist: 'Waist',
      hip: 'Hip',
      inseam: 'Inseam',
      outseam: 'Outseam',
      shoulder: 'Shoulder',
      sleeve: 'Sleeve Length',
      length: 'Length',
      thigh: 'Thigh',
      rise: 'Rise',
      band: 'Band Size',
      overbust: 'Overbust',
      footLength: 'Foot Length',
      footWidth: 'Foot Width',
      circumference: 'Circumference',
      
      // Measurement descriptions
      bustDescription: 'Measure from the stitches below the armpits on one side to another.',
      chestDescription: 'Measure from the stitches below the armpits on one side to another.',
      waistDescription: 'Measure around the natural waistline.',
      hipDescription: 'Measure around the fullest part of your hips.',
      inseamDescription: 'Measure from the crotch to the bottom of the leg.',
      outseamDescription: 'Measure from the top of the waistband to the bottom of the leg.',
      shoulderDescription: 'Measure from one shoulder edge to the other.',
      sleeveDescription: 'Measure from the shoulder seam to the end of the sleeve.',
      lengthDescription: 'Measure from the highest point of the shoulder to the bottom hem.',
      thighDescription: 'Measure around the fullest part of your thigh.',
      riseDescription: 'Measure from the center of the crotch up to the top of the waistband.',
      bandDescription: 'Measure directly under your bust, around your ribcage.',
      overbustDescription: 'Measure around your chest above your bust, under your armpits.',
      footLengthDescription: 'Measure from the heel to the tip of the longest toe.',
      footWidthDescription: 'Measure the widest part of your foot.',
      circumferenceDescription: 'Measure around the widest part.'
    },
    
    // German
    de: {
      // Section titles
      sizeChart: 'Größentabelle',
      howToMeasure: 'So messen Sie richtig',
      fitGuide: 'Passformführer',
      notes: 'Hinweise',
      
      // Fit types
      slim: 'Slim',
      regular: 'Regular',
      loose: 'Loose',
      oversized: 'Oversized',
      
      // Stretch levels
      none: 'Keiner',
      slight: 'Leicht',
      moderate: 'Mittel',
      stretchy: 'Elastisch',
      high: 'Hoch',
      
      // Common measurement points
      bust: 'Brustumfang',
      chest: 'Brustumfang',
      waist: 'Taillenumfang',
      hip: 'Hüftumfang',
      inseam: 'Innenbeinlänge',
      outseam: 'Außenbeinlänge',
      shoulder: 'Schulterbreite',
      sleeve: 'Ärmellänge',
      length: 'Länge',
      thigh: 'Oberschenkelumfang',
      rise: 'Leibhöhe',
      band: 'Unterbrustumfang',
      overbust: 'Oberbrustumfang',
      footLength: 'Fußlänge',
      footWidth: 'Fußbreite',
      circumference: 'Umfang',
      
      // Measurement descriptions
      bustDescription: 'Messen Sie waagerecht über die stärkste Stelle der Brust von Seitennaht zu Seitennaht.',
      chestDescription: 'Messen Sie direkt unter den Achseln, von Seitennaht zu Seitennaht.',
      waistDescription: 'Messen Sie rund um die schmalste Stelle Ihrer Taille.',
      hipDescription: 'Messen Sie rund um die breiteste Stelle Ihres Gesäßes.',
      inseamDescription: 'Messen Sie vom Schritt bis zur Unterkante des Beins.',
      outseamDescription: 'Messen Sie vom oberen Rand des Bundes bis zum unteren Saum.',
      shoulderDescription: 'Messen Sie von einer Schulternaht zur anderen – gerade über den Rücken.',
      sleeveDescription: 'Messen Sie von der Schulternaht bis zum Ende des Ärmels.',
      lengthDescription: 'Messen Sie von der höchsten Schulterstelle bis zum unteren Saum.',
      thighDescription: 'Messen Sie rund um die stärkste Stelle des Oberschenkels.',
      riseDescription: 'Messen Sie vom Schritt bis zur oberen Bundkante.',
      bandDescription: 'Messen Sie direkt unterhalb der Brust, rund um den Brustkorb.',
      overbustDescription: 'Messen Sie oberhalb der Brust, unter den Achseln entlang.',
      footLengthDescription: 'Messen Sie von der Ferse bis zur Spitze des längsten Zehs.',
      footWidthDescription: 'Messen Sie über den breitesten Bereich des Fußes.',
      circumferenceDescription: 'Messen Sie den Umfang an der breitesten Stelle.'
    },
    
    // French
    fr: {
      // Section titles
      sizeChart: 'Guide des Tailles',
      howToMeasure: 'Comment Mesurer',
      fitGuide: 'Guide de Coupe',
      notes: 'Notes',
      
      // Fit types
      slim: 'Ajusté',
      regular: 'Regular',
      loose: 'Ample',
      oversized: 'Oversize',
      
      // Stretch levels
      none: 'Sans Élasticité',
      slight: 'Légère Élasticité',
      moderate: 'Élasticité Modérée',
      stretchy: 'Élastique',
      high: 'Très Élastique',
      
      // Common measurement points
      bust: 'Poitrine',
      chest: 'Poitrine',
      waist: 'Taille',
      hip: 'Hanches',
      inseam: 'Entrejambe',
      outseam: 'Côté Extérieur',
      shoulder: 'Épaules',
      sleeve: 'Longueur de Manche',
      length: 'Longueur',
      thigh: 'Cuisse',
      rise: 'Montant',
      band: 'Tour Sous Poitrine',
      overbust: 'Dessus de Poitrine',
      footLength: 'Longueur du Pied',
      footWidth: 'Largeur du Pied',
      circumference: 'Circonférence',
      
      // Measurement descriptions
      bustDescription: 'Mesurez des coutures sous les aisselles d\'un côté à l\'autre.',
      chestDescription: 'Mesurez des coutures sous les aisselles d\'un côté à l\'autre.',
      waistDescription: 'Mesurez autour de la taille naturelle.',
      hipDescription: 'Mesurez autour de la partie la plus large de vos hanches.',
      inseamDescription: 'Mesurez de l\'entrejambe jusqu\'au bas de la jambe.',
      outseamDescription: 'Mesurez du haut de la ceinture jusqu\'au bas de la jambe.',
      shoulderDescription: 'Mesurez d\'un bord d\'épaule à l\'autre.',
      sleeveDescription: 'Mesurez de la couture d\'épaule jusqu\'à la fin de la manche.',
      lengthDescription: 'Mesurez du point le plus haut de l\'épaule jusqu\'à l\'ourlet inférieur.',
      thighDescription: 'Mesurez autour de la partie la plus large de votre cuisse.',
      riseDescription: 'Mesurez du centre de l\'entrejambe jusqu\'au haut de la ceinture.',
      bandDescription: 'Mesurez directement sous votre poitrine, autour de votre cage thoracique.',
      overbustDescription: 'Mesurez autour de votre poitrine au-dessus du buste, sous les aisselles.',
      footLengthDescription: 'Mesurez du talon jusqu\'à la pointe du plus long orteil.',
      footWidthDescription: 'Mesurez la partie la plus large de votre pied.',
      circumferenceDescription: 'Mesurez autour de la partie la plus large.'
    },
    
    // Spanish
    es: {
      // Section titles
      sizeChart: 'Guía de Tallas',
      howToMeasure: 'Cómo Medir',
      fitGuide: 'Guía de Ajuste',
      notes: 'Notas',
      
      // Fit types
      slim: 'Ajustado',
      regular: 'Regular',
      loose: 'Holgado',
      oversized: 'Oversized',
      
      // Stretch levels
      none: 'Sin Elasticidad',
      slight: 'Ligera Elasticidad',
      moderate: 'Elasticidad Moderada',
      stretchy: 'Elástico',
      high: 'Alta Elasticidad',
      
      // Common measurement points
      bust: 'Busto',
      chest: 'Pecho',
      waist: 'Cintura',
      hip: 'Cadera',
      inseam: 'Entrepierna',
      outseam: 'Largo Exterior',
      shoulder: 'Hombro',
      sleeve: 'Largo de Manga',
      length: 'Largo',
      thigh: 'Muslo',
      rise: 'Tiro',
      band: 'Contorno Bajo Pecho',
      overbust: 'Sobre Busto',
      footLength: 'Largo de Pie',
      footWidth: 'Ancho de Pie',
      circumference: 'Circunferencia',
      
      // Measurement descriptions
      bustDescription: 'Mida desde las costuras debajo de las axilas de un lado a otro.',
      chestDescription: 'Mida desde las costuras debajo de las axilas de un lado a otro.',
      waistDescription: 'Mida alrededor de la cintura natural.',
      hipDescription: 'Mida alrededor de la parte más ancha de sus caderas.',
      inseamDescription: 'Mida desde la entrepierna hasta la parte inferior de la pierna.',
      outseamDescription: 'Mida desde la parte superior de la cintura hasta la parte inferior de la pierna.',
      shoulderDescription: 'Mida de un borde del hombro al otro.',
      sleeveDescription: 'Mida desde la costura del hombro hasta el final de la manga.',
      lengthDescription: 'Mida desde el punto más alto del hombro hasta el dobladillo inferior.',
      thighDescription: 'Mida alrededor de la parte más ancha de su muslo.',
      riseDescription: 'Mida desde el centro de la entrepierna hasta la parte superior de la cintura.',
      bandDescription: 'Mida directamente debajo de su busto, alrededor de su caja torácica.',
      overbustDescription: 'Mida alrededor de su pecho por encima del busto, debajo de las axilas.',
      footLengthDescription: 'Mida desde el talón hasta la punta del dedo más largo.',
      footWidthDescription: 'Mida la parte más ancha de su pie.',
      circumferenceDescription: 'Mida alrededor de la parte más ancha.'
    }
  }
};

// Function to get translation for a specific element
export const getTranslation = (language, key, defaultValue) => {
  // If language is not supported, fall back to English
  if (!defaultTranslations.elements[language]) {
    language = 'en';
  }
  
  // Return the translation or the default value if not found
  return defaultTranslations.elements[language][key] || defaultValue || key;
};

// Function to get available languages from the translations
const getAvailableLanguages = () => {
  // Default languages from the translations
  const defaultLanguages = [
    { label: 'English', value: 'en' },
    { label: 'French', value: 'fr' },
    { label: 'German', value: 'de' },
    { label: 'Spanish', value: 'es' },
    { label: 'Italian', value: 'it' },
    { label: 'Japanese', value: 'ja' },
    { label: 'Chinese (Simplified)', value: 'zh' }
  ];
  
  return defaultLanguages;
};

// Function to translate measurement points
export const translateMeasurementPoints = (measurementPoints, language) => {
  if (!language || language === 'en') {
    return measurementPoints; // Return original if English or no language specified
  }
  
  // Create a deep copy to avoid modifying the original
  const translatedPoints = JSON.parse(JSON.stringify(measurementPoints));
  
  // Translate each garment type's measurement points
  Object.keys(translatedPoints).forEach(garmentType => {
    translatedPoints[garmentType].forEach(point => {
      // Translate label
      const labelKey = point.id;
      point.label = getTranslation(language, labelKey, point.label);
      
      // Translate description
      const descriptionKey = `${labelKey}Description`;
      point.description = getTranslation(language, descriptionKey, point.description);
    });
  });
  
  return translatedPoints;
};

// Function to translate garment types
export const translateGarmentTypes = (garmentTypes, language) => {
  if (!language || language === 'en') {
    return garmentTypes; // Return original if English or no language specified
  }
  
  // Create a deep copy to avoid modifying the original
  const translatedTypes = JSON.parse(JSON.stringify(garmentTypes));
  
  // Custom translations for garment types
  const garmentTypeTranslations = {
    de: {
      'dress': 'Kleid',
      'top': 'Oberteil/Hemd',
      'pants': 'Hose',
      'skirt': 'Rock',
      'jacket': 'Jacke/Mantel',
      'shoes': 'Schuhe',
      'accessories': 'Accessoires',
      'romper': 'Jumpsuit',
      'swimwear': 'Bademode',
      'mens_underwear': 'Herren Unterwäsche',
      'womens_underwear': 'Damen Unterwäsche',
      'bra': 'BH',
      'lingerie_set': 'Lingerie-Set',
      'shapewear': 'Shapewear',
      'two_piece_set': '2-Teiliges Set (Oberteil & Hose)',
      'two_piece_skirt_set': '2-Teiliges Set (Oberteil & Rock)',
      'three_piece_set': '3-Teiliges Set',
      'suit': 'Anzug',
      'tracksuit': 'Trainingsanzug'
    },
    fr: {
      'dress': 'Robe',
      'top': 'Haut/Chemise',
      'pants': 'Pantalon',
      'skirt': 'Jupe',
      'jacket': 'Veste/Manteau',
      'shoes': 'Chaussures',
      'accessories': 'Accessoires',
      'romper': 'Combinaison',
      'swimwear': 'Maillot de Bain',
      'mens_underwear': 'Sous-vêtements Homme',
      'womens_underwear': 'Sous-vêtements Femme',
      'bra': 'Soutien-gorge',
      'lingerie_set': 'Ensemble de Lingerie',
      'shapewear': 'Lingerie Gainante',
      'two_piece_set': 'Ensemble 2 Pièces (Haut & Bas)',
      'two_piece_skirt_set': 'Ensemble 2 Pièces (Haut & Jupe)',
      'three_piece_set': 'Ensemble 3 Pièces',
      'suit': 'Costume',
      'tracksuit': 'Survêtement'
    },
    es: {
      'dress': 'Vestido',
      'top': 'Top/Camisa',
      'pants': 'Pantalones',
      'skirt': 'Falda',
      'jacket': 'Chaqueta/Abrigo',
      'shoes': 'Zapatos',
      'accessories': 'Accesorios',
      'romper': 'Mono/Enterizo',
      'swimwear': 'Traje de Baño',
      'mens_underwear': 'Ropa Interior Hombre',
      'womens_underwear': 'Ropa Interior Mujer',
      'bra': 'Sujetador',
      'lingerie_set': 'Conjunto de Lencería',
      'shapewear': 'Faja',
      'two_piece_set': 'Conjunto 2 Piezas (Top & Pantalón)',
      'two_piece_skirt_set': 'Conjunto 2 Piezas (Top & Falda)',
      'three_piece_set': 'Conjunto 3 Piezas',
      'suit': 'Traje',
      'tracksuit': 'Chándal'
    }
  };
  
  // Apply translations
  translatedTypes.forEach(item => {
    if (garmentTypeTranslations[language] && garmentTypeTranslations[language][item.value]) {
      item.label = garmentTypeTranslations[language][item.value];
    }
  });
  
  return translatedTypes;
};
